/**
 * Triggers ELIO bootloader entry via the custom `cmd_fw_update` (0xF2) command
 * over the Nordic UART Service (NUS) — the same path Android and iOS use.
 *
 * Why not the standard SDK Buttonless DFU service?
 *   In SDK 13.1's `ble_dfu.c`, `bootloader_start()` calls `sd_softdevice_disable()`
 *   while the BLE connection is still active, which can fail silently and leave
 *   the device in app mode. Android (`ToySlideWindow.java`) and iOS
 *   (`BLESlideWindow.m`) both bypass this by sending `cmd_fw_update` over NUS.
 *   The firmware's `on_fw_update` (Firmware_SDK_15.0.0/App/App/main.c:832) does a
 *   graceful disconnect first, then sets GPREGRET = FW_UPDATE_VAL0/VAL1 (0x55/0x88)
 *   and resets — which the elio bootloader recognizes
 *   (mobilian/dfu/bootloader/dfu/nrf_dfu.c:94).
 *
 * Slide-window protocol — minimal subset:
 *   Outgoing TCP DAT  : [flag_byte=0x00, length=1, cmd=0xF2]
 *                       flag_byte bits: flag=DAT(0) | method=TCP(0) | win=0
 *   Incoming CTL ACK  : [flag_byte] with bit0=1 (CTL), cmd=ACK — server confirming our packet
 *   Incoming TCP DAT  : [flag_byte=window<<4, length=1, cmd=0xF2] — server's echo
 *   We must ACK echo : [flag_byte=CTL|cmd_ACK|win<<4] with win = remoteAck (advanced)
 *
 * After we ACK the echo, the firmware's `on_fw_update` loop exits and calls
 * `enter_bootloader()` → graceful BLE disconnect → reset → ELIO_DFU advertising.
 */

const NUS_SERVICE = '6e400001-b5a3-f393-e0a9-e50e24dcca9e';
const NUS_RX_CHAR = '6e400002-b5a3-f393-e0a9-e50e24dcca9e';   // PC writes here
const NUS_TX_CHAR = '6e400003-b5a3-f393-e0a9-e50e24dcca9e';   // notify (PC reads)

const CMD_FW_UPDATE = 0xF2;
const CMD_GET_CONFIG = 0x00;   // read-only handshake — firmware responds with current config

const BPF_DAT = 0;
const BPF_CTL = 1;
const BPM_TCP = 0;
const BPC_ACK = 1;
const SLOT_LEN = 8;   // matches firmware's BLE_PACKET_SLOT_LEN

/** Whether the device exposes the NUS service (i.e. running the elio app). */
const hasNusService = async function (device) {
    try {
        await device.gatt.connect();
        const svc = await device.gatt.getPrimaryService(NUS_SERVICE);
        return !!svc;
    } catch (e) {
        return false;
    }
};

/**
 * Connect to the device's NUS, send `cmd_fw_update`, ACK the firmware's echo,
 * and wait for the BLE disconnect that signals the firmware is rebooting into
 * the bootloader.
 *
 * @param {BluetoothDevice} device
 * @param {(msg: string) => void} [log]
 * @returns {Promise<void>}
 */
const triggerBootloader = async function (device, log) {
    const note = log || (() => undefined);

    if (!device.gatt.connected) {
        await device.gatt.connect();
    }
    note('connected gatt');

    const service = await device.gatt.getPrimaryService(NUS_SERVICE);
    const rx = await service.getCharacteristic(NUS_RX_CHAR);
    const tx = await service.getCharacteristic(NUS_TX_CHAR);
    note('found NUS rx/tx');

    // Slide-window state. Firmware's ackWindow may NOT start at 0 on a fresh
    // BLE connection — SDK 13.1 doesn't reset packet state on connect, and
    // background BLE traffic from the OS may have advanced it. So we adopt the
    // first incoming DAT's window as our remoteAck (adaptive sync).
    let remoteAck = -1;      // -1 = uninitialized; sync with first DAT
    let outAckWindow = 0;
    let echoReceived = false;
    let configReceived = false;
    let lastRtrAt = -1;      // dedupe: don't spam RTR for same window
    let writeChain = Promise.resolve();

    // Web Bluetooth allows only ONE GATT write at a time. We serialize all
    // writes through a promise chain to avoid `GATT operation already in
    // progress` errors when firmware bursts packets faster than we can ACK.
    const queueWrite = data => {
        writeChain = writeChain
            .then(() => rx.writeValue(data))
            .catch(() => {});
        return writeChain;
    };

    const sendAck = win => {
        const b = (BPF_CTL & 0x01) | ((BPC_ACK & 0x07) << 1) | ((win & 0x0F) << 4);
        return queueWrite(new Uint8Array([b]));
    };

    const sendRtr = win => {
        // CTL RTR — "please retransmit from window N"
        const b = (BPF_CTL & 0x01) | ((2 & 0x07) << 1) | ((win & 0x0F) << 4);
        return queueWrite(new Uint8Array([b]));
    };

    const onNotification = evt => {
        const buf = new Uint8Array(evt.target.value.buffer,
            evt.target.value.byteOffset, evt.target.value.byteLength);
        if (buf.length < 1) return;
        const flagByte = buf[0];
        const isCtl = (flagByte & 0x01) === 1;

        if (isCtl) {
            const cmd = (flagByte >> 1) & 0x07;
            const win = (flagByte >> 4) & 0x0F;
            note(`CTL recv cmd=${cmd} win=${win}`);
            // cmd=0 BPC_WIN: firmware asks "where are you?" — respond with ACK
            //   at our current remoteAck so it can drain its send buffer.
            //   Without this response, on_fw_update's wait-for-empty loop
            //   never exits and enter_bootloader is never called.
            if (cmd === 0) {
                const respWin = remoteAck === -1 ? 0 : remoteAck;
                sendAck(respWin);
                note(`WIN→ACK respond win=${respWin}`);
            }
            return;
        }
        const window = (flagByte >> 4) & 0x0F;
        const payload = buf.slice(1);
        const isFwUpdateEcho = payload.length >= 2 &&
            payload[0] === 1 &&
            payload[1] === CMD_FW_UPDATE;
        const isCfgResp = payload.length > 2 && payload[1] === 0x00;

        // Adaptive sync — first DAT seeds our remoteAck.
        if (remoteAck === -1) {
            note(`adaptive sync: remoteAck = ${window} (firmware's ackWindow)`);
            remoteAck = window;
        }

        if (window !== remoteAck) {
            // Send RTR at most once per remoteAck value to avoid flooding.
            if (lastRtrAt !== remoteAck) {
                lastRtrAt = remoteAck;
                note(`DAT window mismatch: got ${window} expected ${remoteAck} → RTR`);
                sendRtr(remoteAck);
            }
            return;
        }
        // Accept: advance remoteAck and ACK.
        const ackedFor = window;
        remoteAck = (remoteAck + 1) % SLOT_LEN;
        lastRtrAt = -1;   // reset RTR dedup since we made progress
        sendAck(remoteAck);
        note(`ACK sent win=${remoteAck} for recv=${ackedFor}${isFwUpdateEcho ? ' (echo)' : isCfgResp ? ' (cfg)' : ''}`);
        if (isFwUpdateEcho) echoReceived = true;
        if (isCfgResp) configReceived = true;
    };

    await tx.startNotifications();
    tx.addEventListener('characteristicvaluechanged', onNotification);
    note('subscribed to TX notifications');

    // Helper: send one TCP DAT packet with [length, ...payload].
    const sendTcp = (frameBytes, win) => {
        const flag = (BPF_DAT & 0x01) | ((BPM_TCP & 0x01) << 1) | ((win & 0x0F) << 4);
        const wire = new Uint8Array(2 + frameBytes.length);
        wire[0] = flag;
        wire[1] = frameBytes.length;
        wire.set(frameBytes, 2);
        return queueWrite(wire);
    };

    // STEP 1 — read-only handshake: cmd_get_config(0x00).
    // Firmware responds with current config (78 B split across 2 packets).
    // (Was previously cmd_set_config(0x01)+0x00, which the firmware
    //  misinterpreted as a config write with all-zero values, wiping
    //  the persistent device name. cmd_get_config has no side effects.)
    note('sending cmd_get_config handshake');
    await sendTcp(new Uint8Array([CMD_GET_CONFIG]), outAckWindow);
    outAckWindow = (outAckWindow + 1) % SLOT_LEN;

    // Wait briefly for the config response so the firmware reaches steady state.
    const waitForConfig = new Promise(resolve => {
        const timer = setInterval(() => {
            if (configReceived) {
                clearInterval(timer);
                resolve('configured');
            }
        }, 100);
        setTimeout(() => {
            clearInterval(timer);
            resolve('handshake-timeout');
        }, 3000);
    });
    const handshakeResult = await waitForConfig;
    note(`handshake result: ${handshakeResult}`);

    // STEP 2 — send cmd_fw_update.
    note('sending cmd_fw_update');
    await sendTcp(new Uint8Array([CMD_FW_UPDATE]), outAckWindow);
    outAckWindow = (outAckWindow + 1) % SLOT_LEN;

    // Wait for the firmware to enter bootloader. Two possible signals:
    //  (a) gattserverdisconnected — firmware's enter_bootloader -> disconnect -> reset
    //  (b) explicit timeout — even without observing disconnect, if we got the echo
    //      and ACKed, the firmware is committed to entering bootloader.
    return new Promise((resolve, reject) => {
        const timeoutMs = 8000;
        let settled = false;

        const cleanup = () => {
            settled = true;
            clearTimeout(timer);
            device.removeEventListener('gattserverdisconnected', onDisconnect);
            tx.removeEventListener('characteristicvaluechanged', onNotification);
        };

        const onDisconnect = () => {
            if (settled) return;
            note('device disconnected (entering bootloader)');
            cleanup();
            resolve();
        };

        const timer = setTimeout(() => {
            if (settled) return;
            cleanup();
            if (echoReceived) {
                // Best-effort: device confirmed receipt; assume it's rebooting.
                note('timeout but echo received — assuming bootloader entry');
                if (device.gatt.connected) device.gatt.disconnect();
                resolve();
            } else {
                if (device.gatt.connected) device.gatt.disconnect();
                reject(new Error('cmd_fw_update timed out without echo'));
            }
        }, timeoutMs);

        device.addEventListener('gattserverdisconnected', onDisconnect);
    });
};

export {triggerBootloader, hasNusService, NUS_SERVICE};
